import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { Flame, Gift, Heart, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";

export default function GiftClaim() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/gifts/${id}`)
      .then((r) => setGift(r.data))
      .catch(() => setGift(null))
      .finally(() => setLoading(false));
  }, [id]);

  const claim = async () => {
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/gifts/${id}/claim`);
      await refresh();
      toast.success("Steamy session unlocked");
      nav(`/companions/${data.companion_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
      setBusy(false);
    }
  };

  if (loading) return <div className="max-w-xl mx-auto px-4 py-24 text-slate-500 text-center">Opening your gift…</div>;
  if (!gift) return <div className="max-w-xl mx-auto px-4 py-24 text-slate-500 text-center">This gift link isn't valid.</div>;

  const claimed = gift.status === "claimed";
  const img = gift.companion?.image_url;

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-10 md:py-16">
      <div className="relative rounded-3xl overflow-hidden border border-rose-500/30 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        {img && <img src={img} alt="" className="w-full aspect-[16/10] object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-[#07080A]/50 to-transparent" />
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full border border-rose-500/50 bg-black/50 backdrop-blur px-3 py-1.5">
          <Gift className="h-3 w-3 text-rose-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-200">You've been gifted</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300 mb-1">{gift.kind_label}</div>
          <h1 data-testid="gift-companion-name" className="font-serif text-3xl md:text-4xl font-bold leading-tight text-white">A 24-hour window with {gift.companion_name}</h1>
        </div>
      </div>

      {gift.note && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0F1117] p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">A note from {gift.from_name}</div>
          <p className="text-sm text-slate-200 font-serif italic">"{gift.note}"</p>
        </div>
      )}

      <div className="mt-6">
        {claimed ? (
          <div data-testid="gift-already-claimed" className="rounded-2xl border border-white/10 bg-[#0F1117] p-6 text-center">
            <Lock className="h-6 w-6 text-slate-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">This gift has already been claimed.</p>
          </div>
        ) : (
          <Button
            data-testid="gift-claim-btn"
            onClick={claim}
            disabled={busy}
            size="lg"
            className="w-full rounded-full bg-gradient-to-r from-rose-500 to-amber-500 hover:opacity-90 text-black font-semibold h-14 text-base"
          >
            <Flame className="h-5 w-5 mr-2" />
            {user ? (busy ? "Unlocking…" : "Claim my Steamy session") : "Sign in to claim"}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        )}
        {!user && !claimed && (
          <p className="mt-3 text-[11px] text-slate-500 text-center font-mono uppercase tracking-widest">Sign in with Google · instant</p>
        )}
      </div>
    </div>
  );
}
